/**
 * @file MeasurementEconomicsSection.jsx
 * @module ui/src/components/tenant-admin/sections/MeasurementEconomicsSection
 * @description
 *   Orchestrates Layer 1 compensation-plan configuration.
 *
 * S810 REWRITE — OI-S810-SECTION-FORM-STATE
 *   - Owns local form state via useState (no onSpineChange/onWeightChange/
 *     onRateTableChange/onQuotaSemanticsChange callbacks to parent during editing).
 *   - Exposes getValues() via useImperativeHandle for one-PATCH save flow.
 *   - useEffect resets form only when plan.planId changes.
 *   - All four Layer 1 sub-components receive local setters, not parent callbacks.
 *
 * @version 1.1.0
 * @since S755
 * @changelog
 *   1.0.0  S755  Initial build
 *   1.1.0  S810  Section-local form state rewrite
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';

import EarningsModelSelector from './EarningsModelSelector';
import RateTableReference from './RateTableReference';
import VariableCompAllocation from './VariableCompAllocation';

const MEASUREMENT_BASIS_OPTIONS = Object.freeze([
  { value: '',        label: 'Select measurement basis' },
  { value: 'arr',     label: 'ARR' },
  { value: 'tcv',     label: 'TCV' },
  { value: 'units',   label: 'Units' },
  { value: 'mrr',     label: 'MRR' },
  { value: 'revenue', label: 'Revenue' },
]);

const styles = {
  root:        { fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif', color: 'rgb(var(--color-text))' },
  sectionCard: { border: '1px solid rgb(var(--color-border))', borderRadius: 8, background: 'rgb(var(--color-surface))', padding: 16, marginBottom: 14 },
  muted:       { color: 'rgb(var(--color-text-muted))' },
  fieldGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginTop: 16 },
  field:       { display: 'flex', flexDirection: 'column', gap: 5 },
  label:       { fontWeight: 600 },
  input:       { width: '100%', minHeight: 38, boxSizing: 'border-box', border: '1px solid rgb(var(--color-border))', borderRadius: 6, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', font: 'inherit', padding: '8px 10px' },
  readonlyInput: { width: '100%', minHeight: 38, boxSizing: 'border-box', border: '1px solid rgb(var(--color-border))', borderRadius: 6, background: 'rgb(var(--color-surface-muted))', color: 'rgb(var(--color-text))', font: 'inherit', padding: '8px 10px' },
  // OI-UI-11 S972: hybrid weight card styles
  hybridWeightGrid:     { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))', gap: 14, marginTop: 16 },
  // S996: hybrid rate table 3-column grid — own-production / team-rollup / plan fallback
  // side by side. min 280px per column so cards stay readable; collapses to 1-col on
  // narrow viewports via auto-fit. marginBottom matches sectionCard spacing.
  hybridRateGrid3col:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 14 },
  hybridSumRow:         { marginTop: 10, fontSize: '0.85rem' },
  hybridSumOk:          { color: 'rgb(var(--color-success, 22 163 74))' },
  hybridSumErr:         { color: 'rgb(var(--color-error, 220 38 38))' },
};

// Canonical spine label map — must match usePlanDocument.js SPINE_LABELS
const SPINE_LABELS = {
  revenue_bookings:       'Revenue / Bookings',
  transaction_commission: 'Transaction Commission',
  volume_quantity:        'Volume / Quantity',
  market_share:           'Market Share / Penetration',
  grr_nrr_retention:      'Retention (GRR / NRR)',
  mbo_objective:          'MBO / Objective',
  margin_profit:          'Margin / Profit',
  bonus_pool:             'Bonus Pool / Allocation',
  pharma_script:          'Pharma / Script Volume',
};

function initFromLayer1(layer1Config) {
  return {
    primarySpine:   layer1Config.primarySpine   ?? '',
    weights:        layer1Config.weights        ?? {},
    rateTableRef:   layer1Config.rateTableRef   ?? { rateTableId: null, version: null, versionLabel: null },
    quotaSemantics: {
      measurementBasis:   layer1Config.quotaSemantics?.measurementBasis   ?? '',
      currencyCode:       layer1Config.quotaSemantics?.currencyCode       ?? '',
      seasonalityProfile: layer1Config.quotaSemantics?.seasonalityProfile ?? null,
      measurementScope:   layer1Config.quotaSemantics?.measurementScope   ?? null,
    },
  };
}

const MeasurementEconomicsSection = forwardRef(function MeasurementEconomicsSection({
  plan,
  layer1Config,
  readOnly,
  validationErrors,
  planId,
  planComponentId,
  // OI-UI-11 S972: planComponent carries calculationMode + hybridIcWeightPct +
  // hybridRollupWeightPct from EP2. Used to conditionally render hybrid weight card.
  planComponent,
}, ref) {
  const [form, setForm] = useState(() => initFromLayer1(layer1Config));

  // OI-UI-11 S972: hybrid weight local state — integers 0–100 for UI display.
  // Initialized from planComponent (EP2 fractions × 100). Default 50/50.
  const isHybrid = planComponent?.calculationMode === 'hybrid';
  const [hybridIcWeight,     setHybridIcWeight]     = useState(() =>
    planComponent?.hybridIcWeightPct     != null
      ? Math.round(planComponent.hybridIcWeightPct     * 100)
      : 50
  );
  const [hybridRollupWeight, setHybridRollupWeight] = useState(() =>
    planComponent?.hybridRollupWeightPct != null
      ? Math.round(planComponent.hybridRollupWeightPct * 100)
      : 50
  );
  const [hybridSaveError, setHybridSaveError] = useState(null);

  // S996 OI-HYBRID-RATE-TABLE-ID-NO-WRITE-PATH: hybrid rate table refs.
  // Each stores a rateTableRef object (same shape as plan-level rateTableRef) or null.
  // Managed by two RateTableReference instances — one per earning stream.
  // Initialized from planComponent (EP2 delivers hybridIcRateTableId / hybridRollupRateTableId).
  const [hybridIcRateTableRef,     setHybridIcRateTableRef]     = useState(() =>
    planComponent?.hybridIcRateTableId
      ? { rateTableId: planComponent.hybridIcRateTableId, rateTableName: planComponent.hybridIcRateTableName ?? null, versionLabel: null, bands: [] }
      : null
  );
  const [hybridRollupRateTableRef, setHybridRollupRateTableRef] = useState(() =>
    planComponent?.hybridRollupRateTableId
      ? { rateTableId: planComponent.hybridRollupRateTableId, rateTableName: planComponent.hybridRollupRateTableName ?? null, versionLabel: null, bands: [] }
      : null
  );

  // Reset only on plan change
  useEffect(() => {
    setForm(initFromLayer1(layer1Config));
    // Reset hybrid weights and rate table IDs from fresh planComponent on plan change
    setHybridIcWeight(
      planComponent?.hybridIcWeightPct != null
        ? Math.round(planComponent.hybridIcWeightPct * 100)
        : 50
    );
    setHybridRollupWeight(
      planComponent?.hybridRollupWeightPct != null
        ? Math.round(planComponent.hybridRollupWeightPct * 100)
        : 50
    );
    setHybridIcRateTableRef(planComponent?.hybridIcRateTableId
      ? { rateTableId: planComponent.hybridIcRateTableId, rateTableName: planComponent.hybridIcRateTableName ?? null, versionLabel: null, bands: [] }
      : null);
    setHybridRollupRateTableRef(planComponent?.hybridRollupRateTableId
      ? { rateTableId: planComponent.hybridRollupRateTableId, rateTableName: planComponent.hybridRollupRateTableName ?? null, versionLabel: null, bands: [] }
      : null);
    setHybridSaveError(null);
  }, [plan.planId]); // eslint-disable-line react-hooks/exhaustive-deps

  // OI-UI-11 S972: sync hybrid weights + rate table IDs when planComponent prop
  // updates after EP2 refetch (usePlanDocument.save() calls load() → new planComponent arrives).
  // planId does not change on save so the plan.planId effect above does not fire.
  // Use JSON.stringify as dep to reliably detect object reference changes.
  useEffect(() => {
    if (!isHybrid) return;
    setHybridIcWeight(
      planComponent?.hybridIcWeightPct != null
        ? Math.round(planComponent.hybridIcWeightPct * 100)
        : 50
    );
    setHybridRollupWeight(
      planComponent?.hybridRollupWeightPct != null
        ? Math.round(planComponent.hybridRollupWeightPct * 100)
        : 50
    );
    // S998: OI-HYBRID-IC-RATE-TABLE-SYNC-RESET — use functional updater (prev =>) so a
    // stale EP2 refetch returning null for hybridIcRateTableId does not overwrite a
    // locally-set ref that hasn't been persisted yet (PATCH fires after this effect).
    // Confirmed root cause S997: rate table 9124 created via EP66 but ID never written
    // because the sync effect cleared hybridIcRateTableRef before save ran.
    setHybridIcRateTableRef(prev =>
      planComponent?.hybridIcRateTableId
        ? { rateTableId: planComponent.hybridIcRateTableId, rateTableName: planComponent.hybridIcRateTableName ?? null, versionLabel: null, bands: [] }
        : prev  // preserve locally-set ref; EP2 hasn't caught up yet
    );
    setHybridRollupRateTableRef(prev =>
      planComponent?.hybridRollupRateTableId
        ? { rateTableId: planComponent.hybridRollupRateTableId, rateTableName: planComponent.hybridRollupRateTableName ?? null, versionLabel: null, bands: [] }
        : prev  // defensive: same pattern for rollup ref
    );
  }, [JSON.stringify(planComponent)]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    async getValues() {
      // OI-UI-11/12 S972: if hybrid, PATCH plan-components first (separate endpoint).
      // Fires before the main plan PATCH so both writes complete atomically from
      // the CA's perspective. EP2 refetch after main save picks up updated values.
      if (isHybrid && planComponentId) {
        const hybridSum = hybridIcWeight + hybridRollupWeight;
        if (hybridSum !== 100) {
          // Block save — return sentinel that PlanDocumentCanvas can detect.
          // Client-side validation already shows the error; this prevents a bad PATCH.
          setHybridSaveError(`Own quota weight + Team rollup weight must sum to 100 (currently ${hybridSum}).`);
          throw new Error(`Hybrid weights must sum to 100 (currently ${hybridSum}).`);
        }
        setHybridSaveError(null);
        try {
          const patchBody = {
            hybridIcWeightPct:     hybridIcWeight,
            hybridRollupWeightPct: hybridRollupWeight,
          };
          // Always include rate table IDs — explicit null clears; value sets.
          // Router partial update semantics: field present in JSON → written.
          patchBody.hybridIcRateTableId     = hybridIcRateTableRef?.rateTableId     ?? null;
          patchBody.hybridRollupRateTableId = hybridRollupRateTableRef?.rateTableId ?? null;

          const res = await fetch(`/api/v1/plan-components/${planComponentId}`, {
            method:      'PATCH',
            credentials: 'include',
            headers:     { 'Content-Type': 'application/json' },
            body: JSON.stringify(patchBody),
          });
          if (!res.ok) {
            const errBody = await res.json().catch(() => ({}));
            const msg = typeof errBody.detail === 'string'
              ? errBody.detail
              : errBody.error?.message ?? `Failed to save hybrid weights (${res.status}).`;
            setHybridSaveError(msg);
            throw new Error(msg);
          }
        } catch (err) {
          // Re-throw so PlanDocumentCanvas.handleSave() sees the failure
          throw err;
        }
      }

      // D9 — translate UI weight shape to canonical PATCH shape.
      // UI stores: { primarySpineWeight: 70, secondaryMeasures: [{key:'mbo',weight:30,...},...] }
      // Server expects: { primary: 70, mbo: 30, nps: 0, adoption: 0 } — whole integers 0–100.
      // Server D9 adapter converts to fractions before writing plan_component weight columns.
      const w = form.weights ?? {};
      let canonicalWeights;
      if (w.primary != null) {
        // Already canonical shape — pass through
        canonicalWeights = {
          primary:  Math.round(Number(w.primary)  ?? 0),
          mbo:      Math.round(Number(w.mbo)      ?? 0),
          nps:      Math.round(Number(w.nps)      ?? 0),
          adoption: Math.round(Number(w.adoption) ?? 0),
        };
      } else {
        // Legacy UI shape — derive canonical shape
        const primaryVal = Math.round(Number(w.primarySpineWeight ?? 100));
        const secondary = (w.secondaryMeasures ?? []);
        const mboVal      = Math.round(Number(secondary.find((m) => m.key === 'mbo')?.weight      ?? 0));
        const npsVal      = Math.round(Number(secondary.find((m) => m.key === 'nps')?.weight      ?? 0));
        const adoptionVal = Math.round(Number(secondary.find((m) => m.key === 'adoption')?.weight ?? 0));
        canonicalWeights = {
          primary:  primaryVal,
          mbo:      mboVal,
          nps:      npsVal,
          adoption: adoptionVal,
        };
      }
      // Return layer1Config with ONLY canonical weight shape — no legacy keys.
      // Storing both primarySpineWeight+secondaryMeasures AND primary/mbo/nps/adoption
      // causes normalizeLayer1Config to take the legacy branch and multiply by 100 again.
      return {
        layer1Config: {
          primarySpine:   form.primarySpine,
          weights:        canonicalWeights,
          rateTableRef:   form.rateTableRef,
          quotaSemantics: form.quotaSemantics,
        },
      };
    },
  }), [form, isHybrid, planComponentId, hybridIcWeight, hybridRollupWeight, hybridIcRateTableRef, hybridRollupRateTableRef]);

  // Local setters passed to sub-components
  // WHY: When spine changes, primarySpineLabel in weights must update too so
  // VariableCompAllocation shows the correct label (not the raw key).
  // Also reset primarySpineWeight to 100 and clear secondary weights so the
  // new spine starts with a clean 100% allocation.
  const handleSpineChange = (spine) => {
    const label = SPINE_LABELS[spine] ?? spine;
    setForm((prev) => ({
      ...prev,
      primarySpine: spine,
      weights: {
        ...prev.weights,
        primarySpineLabel:  label,
        primarySpineWeight: 100,
        secondaryMeasures: (prev.weights.secondaryMeasures ?? []).map((m) => ({
          ...m, weight: 0, enabled: false,
        })),
      },
    }));
  };

  const handleWeightChange = (weights) => {
    setForm((prev) => ({ ...prev, weights }));
  };

  const handleRateTableChange = (ref) => {
    setForm((prev) => ({ ...prev, rateTableRef: ref }));
  };

  const updateQuotaSemantic = (field, value) => {
    setForm((prev) => ({
      ...prev,
      quotaSemantics: { ...prev.quotaSemantics, [field]: value },
    }));
  };

  const { primarySpine, weights, rateTableRef, quotaSemantics } = form;

  const currencyRequiredError =
    Boolean(quotaSemantics.measurementBasis) && !quotaSemantics.currencyCode?.trim();

  return (
    <section style={styles.root} aria-labelledby="measurement-economics-heading">
      <header>
        <h3 id="measurement-economics-heading" style={{ marginBottom: 4 }}>
          Measurement &amp; Economics
        </h3>
        <p style={{ ...styles.muted, marginTop: 0 }}>
          Configure how this plan earns variable compensation and how its quota
          or target semantics are defined.
        </p>
      </header>

      <div style={styles.sectionCard}>
        <EarningsModelSelector
          selectedSpine={primarySpine ?? null}
          hasExistingConfig={Boolean(rateTableRef?.rateTableId)}
          readOnly={readOnly}
          isAuthoring={true}
          onSelect={handleSpineChange}
        />
      </div>

      <div style={styles.sectionCard}>
        <VariableCompAllocation
          primarySpine={primarySpine}
          weights={weights}
          readOnly={readOnly}
          onChange={handleWeightChange}
          validationErrors={validationErrors.weights || {}}
        />
      </div>

      {/* OI-UI-11 S972: Hybrid manager weight split — own quota vs team rollup.
          Visible only when planComponent.calculationMode === 'hybrid'.
          Values stored on plan_component via PATCH /api/v1/plan-components/{id}.
          Client-side validation: must sum to 100.
          readOnly prop respected — inputs disabled when plan is approved/locked. */}
      {isHybrid && (
        <div style={styles.sectionCard} aria-labelledby="hybrid-weight-heading">
          <h4 id="hybrid-weight-heading" style={{ margin: '0 0 4px' }}>
            Player-coach earning split
          </h4>
          <p style={{ ...styles.muted, margin: '0 0 4px', fontSize: '0.85rem' }}>
            This role earns from both personal quota attainment and team rollup.
            Allocate 100% across own quota and team rollup performance.
          </p>
          <div style={styles.hybridWeightGrid}>
            <div style={styles.field}>
              <label htmlFor="hybrid-ic-weight" style={styles.label}>
                Own quota weight %
              </label>
              <input
                id="hybrid-ic-weight"
                type="number"
                min={0}
                max={100}
                step={1}
                value={hybridIcWeight}
                disabled={readOnly}
                onChange={(e) => {
                  const val = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                  setHybridIcWeight(val);
                  setHybridSaveError(null);
                }}
                style={readOnly ? styles.readonlyInput : styles.input}
                aria-describedby={hybridSaveError ? 'hybrid-weight-error' : undefined}
              />
            </div>
            <div style={styles.field}>
              <label htmlFor="hybrid-rollup-weight" style={styles.label}>
                Team rollup weight %
              </label>
              <input
                id="hybrid-rollup-weight"
                type="number"
                min={0}
                max={100}
                step={1}
                value={hybridRollupWeight}
                disabled={readOnly}
                onChange={(e) => {
                  const val = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                  setHybridRollupWeight(val);
                  setHybridSaveError(null);
                }}
                style={readOnly ? styles.readonlyInput : styles.input}
                aria-describedby={hybridSaveError ? 'hybrid-weight-error' : undefined}
              />
            </div>
          </div>
          <div style={styles.hybridSumRow}>
            {(() => {
              const sum = hybridIcWeight + hybridRollupWeight;
              return sum === 100
                ? <span style={styles.hybridSumOk}>✓ Total: 100% — weights are valid</span>
                : <span style={styles.hybridSumErr}>Total: {sum}% — must equal 100%</span>;
            })()}
          </div>
          {hybridSaveError && (
            <div id="hybrid-weight-error" className="modifier-manager-inline-error" role="alert" style={{ marginTop: 8 }}>
              {hybridSaveError}
            </div>
          )}

        </div>
      )}

      {/* S996 OI-HYBRID-RATE-TABLE-ID-NO-WRITE-PATH: per-stream rate table pickers.
          Hybrid only: own-production + team-rollup + plan-level fallback rendered
          side-by-side in a 3-column auto-fit grid (collapses to 1-col on narrow
          viewports). Non-hybrid: plan-level card only, rendered as a full-width sectionCard.
          All three use RateTableReference — identical drawer/preview/version UX. */}
      {isHybrid ? (
        <div style={styles.hybridRateGrid3col} role="group" aria-label="Rate table configuration">
          <RateTableReference
            rateTableRef={hybridIcRateTableRef}
            planId={planId}
            planComponentId={planComponentId}
            planSpine={primarySpine}
            planStatus={plan.planStatus ?? plan.plan_status ?? 'draft'}
            readOnly={readOnly}
            label="Own-production rate table"
            contextNote="Payout curve for this manager's personal quota attainment."
            onSelect={(ref) => setHybridIcRateTableRef(ref)}
            hybridSlot="ic"
          />
          <RateTableReference
            rateTableRef={hybridRollupRateTableRef}
            planId={planId}
            planComponentId={planComponentId}
            planSpine={primarySpine}
            planStatus={plan.planStatus ?? plan.plan_status ?? 'draft'}
            readOnly={readOnly}
            label="Team rollup rate table"
            contextNote="Payout curve for this manager's team rollup earnings."
            onSelect={(ref) => setHybridRollupRateTableRef(ref)}
            hybridSlot="rollup"
          />
          <RateTableReference
            rateTableRef={rateTableRef}
            planId={planId}
            planComponentId={planComponentId}
            planSpine={primarySpine}
            planStatus={plan.planStatus ?? plan.plan_status ?? 'draft'}
            readOnly={readOnly}
            label="Plan-level rate table (fallback)"
            contextNote="Used when no dedicated rate table is assigned above."
            onSelect={handleRateTableChange}
            validationError={validationErrors.rateTableRef}
          />
        </div>
      ) : (
        /* Non-hybrid: single full-width rate table card */
        <div style={styles.sectionCard}>
          <RateTableReference
            rateTableRef={rateTableRef}
            planId={planId}
            planComponentId={planComponentId}
            planSpine={primarySpine}
            planStatus={plan.planStatus ?? plan.plan_status ?? 'draft'}
            readOnly={readOnly}
            onSelect={handleRateTableChange}
            validationError={validationErrors.rateTableRef}
          />
        </div>
      )}

      {/* S812: Quota semantics always visible — required fields for Performance & Rates completion.
          Was previously hidden behind an accordion (incorrect — required fields must not be hidden). */}
      <div style={styles.sectionCard}>
        <h4 style={{ margin: '0 0 4px' }}>Quota / Target Semantics</h4>
        <p style={{ ...styles.muted, margin: '0 0 12px', fontSize: '0.85rem' }}>
          Quota values per rep are loaded separately and do not require a plan amendment to change.
          This section defines how quota is measured and denominated.
        </p>

            <div style={styles.fieldGrid}>
              <div style={styles.field}>
                <label htmlFor="measurement-basis" style={styles.label}>
                  Measurement Basis
                </label>
                <select
                  id="measurement-basis"
                  value={quotaSemantics.measurementBasis || ''}
                  disabled={readOnly}
                  onChange={(e) => updateQuotaSemantic('measurementBasis', e.target.value)}
                  style={readOnly ? styles.readonlyInput : styles.input}
                >
                  {MEASUREMENT_BASIS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {validationErrors['quotaSemantics.measurementBasis'] && (
                  <div className="modifier-manager-inline-error" role="alert">
                    {validationErrors['quotaSemantics.measurementBasis']}
                  </div>
                )}
              </div>

              <div style={styles.field}>
                <label htmlFor="quota-currency-code" style={styles.label}>
                  Quota Currency Code
                </label>
                <input
                  id="quota-currency-code"
                  type="text"
                  value={quotaSemantics.currencyCode || ''}
                  maxLength={3}
                  autoCapitalize="characters"
                  disabled={readOnly}
                  aria-invalid={currencyRequiredError || Boolean(validationErrors['quotaSemantics.currencyCode'])}
                  aria-describedby={
                    currencyRequiredError || validationErrors['quotaSemantics.currencyCode']
                      ? 'quota-currency-code-error'
                      : undefined
                  }
                  onChange={(e) => updateQuotaSemantic('currencyCode', e.target.value.toUpperCase())}
                  placeholder="USD"
                  style={readOnly ? styles.readonlyInput : styles.input}
                />
                {(currencyRequiredError || validationErrors['quotaSemantics.currencyCode']) && (
                  <div id="quota-currency-code-error" className="modifier-manager-inline-error" role="alert">
                    {validationErrors['quotaSemantics.currencyCode'] ||
                      'Quota currency code is required when a measurement basis is configured.'}
                  </div>
                )}
              </div>

              <div style={styles.field}>
                <label htmlFor="seasonality-profile" style={styles.label}>
                  Seasonality Profile
                </label>
                <input
                  id="seasonality-profile"
                  type="text"
                  value={quotaSemantics.seasonalityProfile || ''}
                  disabled={readOnly}
                  onChange={(e) => updateQuotaSemantic('seasonalityProfile', e.target.value || null)}
                  placeholder="Optional profile"
                  style={readOnly ? styles.readonlyInput : styles.input}
                />
                {validationErrors['quotaSemantics.seasonalityProfile'] && (
                  <div className="modifier-manager-inline-error" role="alert">
                    {validationErrors['quotaSemantics.seasonalityProfile']}
                  </div>
                )}
              </div>

              <div style={styles.field}>
                <label htmlFor="measurement-scope" style={styles.label}>
                  Measurement Scope
                </label>
                <input
                  id="measurement-scope"
                  type="text"
                  value={quotaSemantics.measurementScope || ''}
                  disabled={readOnly}
                  onChange={(e) => updateQuotaSemantic('measurementScope', e.target.value || null)}
                  placeholder="Optional scope qualifier"
                  style={readOnly ? styles.readonlyInput : styles.input}
                />
                {validationErrors['quotaSemantics.measurementScope'] && (
                  <div className="modifier-manager-inline-error" role="alert">
                    {validationErrors['quotaSemantics.measurementScope']}
                  </div>
                )}
              </div>
            </div>
      </div>
    </section>
  );
});

MeasurementEconomicsSection.displayName = 'MeasurementEconomicsSection';

MeasurementEconomicsSection.propTypes = {
  plan: PropTypes.shape({
    planId:      PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    planStatus:  PropTypes.string,
    plan_status: PropTypes.string,
  }).isRequired,
  planId:          PropTypes.number,
  planComponentId: PropTypes.number,
  // OI-UI-11 S972 + S996: planComponent from EP2 — carries calculationMode,
  // hybrid weights, and hybrid rate table fields (S996 addition).
  // hybridIcRateTableName / hybridRollupRateTableName are display-only — used to
  // pre-populate the rate table ref name on init without a second fetch.
  planComponent: PropTypes.shape({
    calculationMode:           PropTypes.string,
    hybridIcWeightPct:         PropTypes.number,
    hybridRollupWeightPct:     PropTypes.number,
    hybridIcRateTableId:       PropTypes.number,
    hybridIcRateTableName:     PropTypes.string,
    hybridRollupRateTableId:   PropTypes.number,
    hybridRollupRateTableName: PropTypes.string,
  }),
  layer1Config: PropTypes.shape({
    primarySpine:   PropTypes.string.isRequired,
    weights:        PropTypes.object.isRequired,
    rateTableRef:   PropTypes.object.isRequired,
    quotaSemantics: PropTypes.shape({
      measurementBasis:   PropTypes.oneOf(['', 'arr', 'tcv', 'units', 'mrr', 'revenue']).isRequired,
      currencyCode:       PropTypes.string.isRequired,
      seasonalityProfile: PropTypes.string,
      measurementScope:   PropTypes.string,
    }).isRequired,
  }).isRequired,
  readOnly:         PropTypes.bool.isRequired,
  validationErrors: PropTypes.object.isRequired,
};

export default MeasurementEconomicsSection;
